import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import axios from 'axios'
import { KTIcon } from '../../../helpers'
import { useAuth } from '../../../../app/modules/auth'

export interface ActivityItem {
  id: string
  title: string
  description: string
  time: string
  timestamp?: string
  type: 'invoice' | 'cdr' | 'rate' | 'warning' | 'security' | string
  category: 'All' | 'Product' | 'Client' | 'Billing' | 'CDR' | 'Invoice' | 'Users' | 'Role' | 'Settings' | string
  isRead: boolean
}

const initialActivities: ActivityItem[] = [
  {
    id: 'act-1',
    title: 'Invoice Bulanan Siap',
    description: 'Rekapitulasi tagihan periode Desember 2025 siap ditinjau.',
    time: 'Des 2025',
    type: 'invoice',
    category: 'Invoice',
    isRead: false,
  },
  {
    id: 'act-2',
    title: 'CDR Batch Ingested',
    description: '247,154 data panggilan tersinkronisasi di sistem billing.',
    time: 'Des 2025',
    type: 'cdr',
    category: 'CDR',
    isRead: false,
  },
  {
    id: 'act-3',
    title: 'Enterprise Client Aktif',
    description: 'Akun korporat enterprise aktif dengan pengaturan saluran SIP Trunk.',
    time: '1h lalu',
    type: 'warning',
    category: 'Client',
    isRead: false,
  },
  {
    id: 'act-4',
    title: 'Tarif Provider Diperbarui',
    description: 'Konfigurasi tarif operator telekomunikasi aktif di sistem.',
    time: '3h lalu',
    type: 'rate',
    category: 'Billing',
    isRead: true,
  },
  {
    id: 'act-5',
    title: 'Sesi Autentikasi Login',
    description: 'Sesi login berhasil diautentikasi ke sistem Smart Billing.',
    time: '5h lalu',
    type: 'security',
    category: 'Users',
    isRead: true,
  },
]

interface HeaderActivityLogProps {
  variant?: 'navbar' | 'wallboard'
}

export const HeaderActivityLog: React.FC<HeaderActivityLogProps> = ({ variant = 'navbar' }) => {
  const { currentUser } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [activities, setActivities] = useState<ActivityItem[]>(initialActivities)
  const [selectedFilter, setSelectedFilter] = useState<'All' | 'Product' | 'Client' | 'Billing' | 'CDR' | 'Invoice' | 'Users' | 'Role' | 'Settings' | string>('All')
  const containerRef = useRef<HTMLDivElement>(null)

  const storageKey = useMemo(() => {
    return currentUser?.id ? `smart_billing_read_activities_${currentUser.id}` : 'smart_billing_read_activities'
  }, [currentUser?.id])

  const fetchActivities = useCallback(async () => {
    try {
      setIsLoading(true)
      const API_URL =
        process.env.REACT_APP_THEME_API_URL ||
        process.env.REACT_APP_API_URL

      const res = await axios.get(`${API_URL}/activities`)
      const rawData = res.data?.data || res.data?.Data || res.data || []

      if (Array.isArray(rawData) && rawData.length > 0) {
        let readIds: string[] = []
        try {
          const stored = localStorage.getItem(storageKey)
          if (stored) readIds = JSON.parse(stored)
        } catch (err) {
          console.warn('Failed to parse read activity ids', err)
        }

        const mapped: ActivityItem[] = rawData.map((item: any) => {
          const itemId = String(item.id || item.Id)
          return {
            id: itemId,
            title: item.title || item.Title,
            description: item.description || item.Description,
            time: item.time || item.Time,
            timestamp: item.timestamp || item.Timestamp,
            type: item.type || item.Type || 'security',
            category: item.category || item.Category || 'System',
            isRead: readIds.includes(itemId),
          }
        })

        setActivities(mapped)
      }
    } catch (error) {
      console.warn('Could not fetch live activities, using fallback', error)
    } finally {
      setIsLoading(false)
    }
  }, [storageKey])

  useEffect(() => {
    fetchActivities()
  }, [fetchActivities])

  useEffect(() => {
    if (isOpen) {
      fetchActivities()
    }
  }, [isOpen, fetchActivities])

  const unreadCount = useMemo(() => {
    return activities.filter((a) => !a.isRead).length
  }, [activities])

  const filteredActivities = useMemo(() => {
    if (selectedFilter === 'All') return activities
    if (selectedFilter.toLowerCase() === 'product') {
      return activities.filter((a) => {
        const cat = (a.category || '').toLowerCase()
        return cat === 'product' || cat === 'products'
      })
    }
    return activities.filter((a) => (a.category || '').toLowerCase() === selectedFilter.toLowerCase())
  }, [activities, selectedFilter])

  // Handle outside click & Esc
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }

    const handleCloseOthers = (e: any) => {
      if (e?.detail !== 'activityLog') {
        setIsOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('ccs-close-header-dropdowns', handleCloseOthers)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('ccs-close-header-dropdowns', handleCloseOthers)
    }
  }, [])

  const handleToggle = () => {
    if (!isOpen) {
      window.dispatchEvent(new CustomEvent('ccs-close-header-dropdowns', { detail: 'activityLog' }))
    }
    setIsOpen((prev) => !prev)
  }

  const markAllAsRead = (e: React.MouseEvent) => {
    e.stopPropagation()
    const allIds = activities.map((item) => item.id)
    try {
      localStorage.setItem(storageKey, JSON.stringify(allIds))
    } catch (err) {}
    setActivities((prev) => prev.map((item) => ({ ...item, isRead: true })))
  }

  const markAsRead = (id: string) => {
    try {
      const stored = localStorage.getItem(storageKey)
      const readIds: string[] = stored ? JSON.parse(stored) : []
      if (!readIds.includes(id)) {
        readIds.push(id)
        localStorage.setItem(storageKey, JSON.stringify(readIds))
      }
    } catch (err) {}
    setActivities((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isRead: true } : item))
    )
  }

  return (
    <div
      ref={containerRef}
      className={clsx(
        'ccs-activity-log-wrapper position-relative',
        variant === 'wallboard' ? 'ccs-wallboard-bell' : 'ccs-navbar-bell'
      )}
    >
      {/* Activity Log Button */}
      <button
        type='button'
        className={clsx('ccs-activity-bell-btn border-0 position-relative', isOpen && 'active')}
        onClick={handleToggle}
        title='Activity Log'
        aria-label='Activity Log'
      >
        {/* Activity Log / History List with Clock Icon */}
        <svg
          className='ccs-activity-icon'
          width='20'
          height='20'
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='2.2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          {/* Top header bar */}
          <line x1='3' y1='5' x2='21' y2='5'></line>
          {/* List item rows */}
          <line x1='3' y1='11' x2='9' y2='11'></line>
          <line x1='3' y1='17' x2='9' y2='17'></line>
          {/* Time / Clock indicator */}
          <circle cx='16.5' cy='14.5' r='5.5'></circle>
          <polyline points='16.5 12 16.5 14.5 18.5 15.5'></polyline>
        </svg>

        {/* Counter Badge */}
        {unreadCount > 0 && (
          <span className='ccs-bell-badge d-flex align-items-center justify-content-center'>
            {unreadCount}
          </span>
        )}
      </button>

      {/* Activity Log Dropdown Menu */}
      {isOpen && (
        <div
          className={clsx(
            'ccs-activity-dropdown-menu position-absolute mt-2 shadow-lg',
            variant === 'wallboard' ? 'end-0' : 'end-0'
          )}
        >
          {/* Header */}
          <div className='ccs-activity-dropdown-header d-flex align-items-center justify-content-between p-3 border-bottom'>
            <div className='d-flex align-items-center gap-2'>
              <div className='d-flex align-items-center justify-content-center text-primary'>
                <svg
                  width='22'
                  height='22'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2.2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <line x1='3' y1='5' x2='21' y2='5'></line>
                  <line x1='3' y1='11' x2='9' y2='11'></line>
                  <line x1='3' y1='17' x2='9' y2='17'></line>
                  <circle cx='16.5' cy='14.5' r='5.5'></circle>
                  <polyline points='16.5 12 16.5 14.5 18.5 15.5'></polyline>
                </svg>
              </div>
              <div>
                <h6 className='mb-0 fw-bold ccs-header-title'>Activity Log</h6>
                <span className='fs-8 text-muted'>Semua aktivitas sistem terbaru</span>
              </div>
            </div>

            {unreadCount > 0 ? (
              <button
                type='button'
                className='btn btn-sm btn-link p-0 fs-8 fw-bold text-primary text-decoration-none'
                onClick={markAllAsRead}
              >
                Tandai Dibaca
              </button>
            ) : (
              <span className='badge bg-light-success text-success fs-9 rounded-pill px-2 py-1'>
                Semua Terbaca
              </span>
            )}
          </div>

          {/* Filter Tabs */}
          <div className='ccs-activity-tabs d-flex align-items-center gap-1 px-3 py-2 border-bottom flex-wrap'>
            {(['All', 'Product', 'Client', 'Billing', 'CDR', 'Invoice', 'Users', 'Role', 'Settings'] as const).map((tab) => (
              <button
                key={tab}
                type='button'
                className={clsx(
                  'ccs-act-tab-btn btn btn-sm py-1 px-2.5 rounded-pill fs-8 fw-bold border-0',
                  selectedFilter === tab && 'active'
                )}
                onClick={() => setSelectedFilter(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Activity List */}
          <div className='ccs-activity-list py-1 activity-list-scroll'>
            {isLoading && activities.length === 0 ? (
              <div className='text-center py-5 text-muted fs-8 d-flex align-items-center justify-content-center gap-2'>
                <span className='spinner-border spinner-border-sm text-primary'></span>
                <span>Memuat aktivitas...</span>
              </div>
            ) : filteredActivities.length === 0 ? (
              <div className='text-center py-5 text-muted fs-8'>Tidak ada log aktivitas.</div>
            ) : (
              filteredActivities.map((act) => (
                <div
                  key={act.id}
                  className={clsx(
                    'ccs-activity-item d-flex align-items-start gap-2.5 px-3 py-2.5 cursor-pointer',
                    !act.isRead && 'is-unread'
                  )}
                  onClick={() => markAsRead(act.id)}
                >
                  {/* Category / Event Icon */}
                  <div className={clsx('ccs-act-icon-box flex-shrink-0 mt-0.5', `type-${act.type}`)}>
                    {(act.category === 'Product' || act.category === 'Products') && '📦'}
                    {act.category === 'Client' && '🏢'}
                    {act.category === 'Billing' && '💵'}
                    {act.category === 'CDR' && '⚡'}
                    {act.category === 'Invoice' && '📄'}
                    {act.category === 'Users' && '👤'}
                    {act.category === 'Role' && '🛡️'}
                    {act.category === 'Settings' && '⚙️'}
                    {!['Product', 'Products', 'Client', 'Billing', 'CDR', 'Invoice', 'Users', 'Role', 'Settings'].includes(act.category) && (
                      act.type === 'invoice' ? '📄' : act.type === 'cdr' ? '⚡' : act.type === 'rate' ? '💵' : act.type === 'warning' ? '⚠️' : '⚙️'
                    )}
                  </div>

                  {/* Content */}
                  <div className='d-flex flex-column flex-grow-1 min-w-0'>
                    <div className='d-flex align-items-center justify-content-between gap-1'>
                      <span className='ccs-act-title fw-bold fs-7 text-truncate'>{act.title}</span>
                      <span className='ccs-act-time fs-9 text-muted flex-shrink-0'>{act.time}</span>
                    </div>
                    <span className='ccs-act-desc fs-8 text-muted mt-0.5'>{act.description}</span>
                  </div>

                  {/* Unread Dot */}
                  {!act.isRead && (
                    <span className='ccs-act-unread-dot flex-shrink-0 mt-1.5' title='Belum dibaca'></span>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className='ccs-activity-dropdown-footer d-flex align-items-center justify-content-between px-3 py-2 border-top fs-8 text-muted'>
            <span
              className='fw-bold text-primary cursor-pointer d-flex align-items-center gap-1 hover-opacity-75'
              onClick={() => {
                setIsOpen(false)
                navigate('/activity-log')
              }}
              title='Buka Halaman Lengkap Activity Log'
            >
              <span>Lihat Semua Aktivitas</span>
              <span>→</span>
            </span>
            <span className='fw-semibold text-muted cursor-pointer' onClick={() => setIsOpen(false)}>
              Tutup ✕
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
